import { SubscriptionAccess } from '../types/Subscription';
import { ExamRole, UserExam } from '../types/User';
import {
  type ExamWorkloadItem as SharedExamWorkloadItem,
  getDefaultWorkload,
  getExamWorkloadConfig,
  getWorkloadTiers,
  NCLEX_EXAM_ID,
  resolveWorkloadTier,
  type YieldFilterKey,
  type WorkloadTierId as SharedWorkloadTierId,
} from '../config/examConfig';

export const STEP1_EXAM_ID = '8412aecf-480b-4d6a-ad55-e98b08bc21c8';
export const PRECLINICAL_EXAM_ID = '7635c078-433a-4063-aa76-f19cddcfaea3';
export { NCLEX_EXAM_ID };

export type ExamWorkloadItem = SharedExamWorkloadItem;
export type WorkloadTierId = SharedWorkloadTierId;

export const EXAM_CONFIG_TYPES = {
  NBME: 'NBME',
  COMLEX: 'COMLEX',
  RESIDENCY: 'RESIDENCY',
  PRE_CLINICAL: 'PRE_CLINICAL',
  CUSTOM: 'CUSTOM',
  NCLEX: 'NCLEX',
} as const;

export const ExamType = {
  NBME: EXAM_CONFIG_TYPES.NBME,
  COMLEX: EXAM_CONFIG_TYPES.COMLEX,
  RESIDENCY: EXAM_CONFIG_TYPES.RESIDENCY,
  PRE_CLINICAL: EXAM_CONFIG_TYPES.PRE_CLINICAL,
  CUSTOM: EXAM_CONFIG_TYPES.CUSTOM,
  NCLEX: EXAM_CONFIG_TYPES.NCLEX,
} as const;

export type ExamType =
  (typeof EXAM_CONFIG_TYPES)[keyof typeof EXAM_CONFIG_TYPES];

export const getYieldFilter = (examId: string): YieldFilterKey => {
  return getExamWorkloadConfig(examId)?.yieldFilter ?? 'yield';
};

export const isNbmeExamId = (examId?: string | null) => {
  if (!examId) return false;
  const config = getExamWorkloadConfig(examId);
  return config?.type === ExamType.NBME;
};

export const isNbmeExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  return exam.type === ExamType.NBME || isNbmeExamId(exam.id);
};

export const isStepExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  if (exam?.type === ExamType.NBME) {
    return exam.name.includes('Step');
  }
  return false;
};

export const isShelfExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  if (exam?.type === ExamType.NBME) {
    return exam.name.includes('Shelf');
  }
  return false;
};

export const isComlexExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  return exam.type === ExamType.COMLEX;
};

export const isResidencyExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  return exam.type === ExamType.RESIDENCY;
};

// NCLEX-RN (nursing) has its own type, so every medical allow-list keyed on
// NBME/COMLEX (Step and Shelf grouping, Content Sets, NBME-only chrome) fails
// closed for it. Branch on this helper where nursing needs its own behavior.
export const isNclexExamId = (examId?: string | null) => {
  if (!examId) return false;
  return getExamWorkloadConfig(examId)?.type === ExamType.NCLEX;
};

// Falls back to the config by id, like `isNbmeExam`, so a payload without
// `type` still resolves the same way as the id-keyed checks.
export const isNclexExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  return exam.type === ExamType.NCLEX || isNclexExamId(exam.id);
};

export const isTodaySessionExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  return (
    exam.type === ExamType.NBME ||
    exam.type === ExamType.COMLEX ||
    exam.type === ExamType.RESIDENCY ||
    isNclexExam(exam)
  );
};

// True only for an exam whose config turns videos off by default (NCLEX-RN
// has no videos at launch). Medical exams keep today's behavior.
export const isVideosOffForExam = (exam?: UserExam | null) => {
  if (!exam?.id) return false;
  return getExamWorkloadConfig(exam.id)?.defaultUsingVideos === false;
};

// The exam name in the companion's "Give me ___ questions" chip (ORA-952 D3):
// the NCLEX exam's short label, and USMLE for every medical exam as before.
export const getQuestionPromptLabel = (exam?: UserExam | null): string => {
  if (!isNclexExam(exam)) return 'USMLE';
  return getExamWorkloadConfig(exam?.id)?.shortLabel ?? 'NCLEX';
};

export const getExamInitials = (name: string) => {
  const insignificantWords = new Set(['&', 'and', 'of']);

  return name
    .trim()
    .split(/\s+/)
    .filter((word) => !insignificantWords.has(word.toLowerCase()))
    .slice(0, 2)
    .map((word) => word.match(/[a-z0-9]/i)?.[0] ?? '')
    .join('')
    .toUpperCase();
};

export const isCustomExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  return exam.type === ExamType.CUSTOM;
};

/** A partner course (Courses V1): an exam that is a prescriptive path. */
export const isCourseExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  return exam.type === 'COURSE';
};

export const isPreclinicalExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  return exam.type === ExamType.PRE_CLINICAL;
};

export const isPreclinicalModeExam = (exam?: UserExam | null) =>
  isCustomExam(exam) || isPreclinicalExam(exam);

export const isSharedCustomExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  if (exam?.type !== ExamType.CUSTOM) return false;
  if (exam.sharedWithSchool) {
    return true;
  }
  return false;
};

export const isVerifiedSchoolExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  if (exam?.type === ExamType.CUSTOM && exam.verified) {
    return true;
  }
  return false;
};

export const isPersonalExam = (exam?: UserExam | null) => {
  if (!exam) return false;
  if (
    exam?.type === ExamType.CUSTOM &&
    exam.role === ExamRole.OWNER &&
    !exam.sharedWithSchool
  ) {
    return true;
  }
  return false;
};

export const getExamAccessLevel = (exam?: UserExam | null) => {
  if (!exam) return SubscriptionAccess.PRE_CLINICAL;

  if (!getExamWorkloadConfig(exam.id)) {
    return SubscriptionAccess.PRE_CLINICAL;
  }

  if (
    isNbmeExam(exam) ||
    isComlexExam(exam) ||
    isResidencyExam(exam) ||
    isPreclinicalExam(exam) ||
    isNclexExam(exam)
  ) {
    const accessLevel = getExamWorkloadConfig(exam.id)?.accessLevel;
    if (accessLevel === 'TRIAL') return SubscriptionAccess.TRIAL;
    if (accessLevel === 'FREE') return SubscriptionAccess.FREE;
    if (accessLevel === 'STEP_SHELF') return SubscriptionAccess.STEP_SHELF;
    return SubscriptionAccess.PRE_CLINICAL;
  }

  return SubscriptionAccess.PRE_CLINICAL;
};

const getWorkloadItem = (
  exam: UserExam | null | undefined,
  workloadTier: WorkloadTierId
): ExamWorkloadItem | undefined => {
  if (!exam?.id) {
    return undefined;
  }

  const workloads = getWorkloadTiers(exam.id);
  if (!workloads.length) {
    return undefined;
  }

  return (
    workloads.find((workload) => workload.tier === workloadTier) ?? workloads[0]
  );
};

// Convenience functions for specific quantities
export const getHighYieldFlashcards = (exam?: UserExam) =>
  getWorkloadItem(exam, 'high_yield')?.flashcards ?? 0;

export const getHighYieldQbank = (exam?: UserExam) =>
  getWorkloadItem(exam, 'high_yield')?.vignettes ?? 0;
export const getStandardQbank = (exam?: UserExam) =>
  getWorkloadItem(exam, 'standard')?.vignettes ?? 0;

export const defaultFlashcards = (exam?: UserExam) =>
  getHighYieldFlashcards(exam);
export const defaultVignettes = (exam?: UserExam) => getHighYieldQbank(exam);

export const getWorkloadTiersForExam = (exam?: UserExam | null) => {
  if (!exam?.id) return [];
  return getWorkloadTiers(exam.id);
};

export const hasMultipleWorkloadTiers = (exam?: UserExam | null) => {
  return getWorkloadTiersForExam(exam).length > 1;
};

export const getWorkloadByLabel = (
  exam: UserExam | null | undefined,
  workloadLabel: string
) => {
  if (!workloadLabel) {
    return undefined;
  }
  return getWorkloadTiersForExam(exam).find(
    (workload) => workload.label === workloadLabel
  );
};

// Per-content-type workload resolvers. Each derives a tier from only its own
// selected count (the other count is nulled out so resolveWorkloadTier can't
// cross-read it), so QBank and Flashcards can sit on independent tiers. Mirrors
// the backend `resolveWorkloadPoolConfig` split.
export const getResolvedQbankWorkload = (params: {
  exam?: UserExam | null;
  selectedVignetteCount: number;
}): ExamWorkloadItem | null => {
  const { exam, selectedVignetteCount } = params;
  return (
    resolveWorkloadTier({
      examId: exam?.id,
      selectedVignetteCount,
      selectedFlashcardCount: null,
    }) ??
    getDefaultWorkload(getWorkloadTiersForExam(exam)) ??
    null
  );
};

export const getResolvedFlashcardWorkload = (params: {
  exam?: UserExam | null;
  selectedFlashcardCount: number;
}): ExamWorkloadItem | null => {
  const { exam, selectedFlashcardCount } = params;
  return (
    resolveWorkloadTier({
      examId: exam?.id,
      selectedVignetteCount: null,
      selectedFlashcardCount,
    }) ??
    getDefaultWorkload(getWorkloadTiersForExam(exam)) ??
    null
  );
};

// Legacy single-workload resolver: prioritizes vignettes, then flashcards, then
// the exam default. This is the tier the app showed and served before per-type
// workloads existed.
export const getResolvedWorkloadForCounts = (params: {
  exam?: UserExam | null;
  selectedFlashcardCount: number;
  selectedVignetteCount: number;
}): ExamWorkloadItem | null => {
  const { exam, selectedFlashcardCount, selectedVignetteCount } = params;
  return (
    resolveWorkloadTier({
      examId: exam?.id,
      selectedFlashcardCount,
      selectedVignetteCount,
    }) ??
    getDefaultWorkload(getWorkloadTiersForExam(exam)) ??
    null
  );
};

// Resolve the tiers to DISPLAY for the two selectors. Independent per-type tiers
// are honored only when both counts are exact canonical tier values — which is
// what the split UI writes when a user actively picks. Otherwise (legacy/drifted
// counts, or one count unset) both fall back to the combined tier, so existing
// users keep showing — and are served — their single pre-split workload with no
// backfill. Mirrors the backend `resolveWorkloadPoolConfig` gate exactly.
export const getResolvedWorkloadPair = (params: {
  exam?: UserExam | null;
  selectedVignetteCount: number;
  selectedFlashcardCount: number;
}): { qbank: ExamWorkloadItem | null; flashcard: ExamWorkloadItem | null } => {
  const { exam, selectedVignetteCount, selectedFlashcardCount } = params;
  const qbank = getResolvedQbankWorkload({ exam, selectedVignetteCount });

  const flashcard = getResolvedFlashcardWorkload({
    exam,
    selectedFlashcardCount,
  });

  const combined = getResolvedWorkloadForCounts({
    exam,
    selectedVignetteCount,
    selectedFlashcardCount,
  });

  const isExactTierPair =
    !!qbank &&
    !!flashcard &&
    selectedVignetteCount === qbank.vignettes &&
    selectedFlashcardCount === flashcard.flashcards;

  if (isExactTierPair) {
    return { qbank, flashcard };
  }
  return { qbank: combined, flashcard: combined };
};

export const getWorkloadByTier = (
  exam?: UserExam | null,
  workloadTier?: WorkloadTierId | null
): ExamWorkloadItem | undefined => {
  if (!workloadTier) return undefined;
  return getWorkloadItem(exam, workloadTier);
};
